import { Controller, useFormContext } from "react-hook-form";
import type { Option } from "Models";
import { Field, FieldLabel, FieldDescription } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type Primitive = string | number | boolean;

interface RoundSettingSelectProps {
  /** RHF field path, e.g. "settings.pinType". */
  name: string;
  label: string;
  /** Canonical option data (roundConfigEnumsAsReactSelectOptions.*). */
  options: Option<Primitive>[];
  description?: string;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * Reusable, shadcn-styled, RHF-bound select for round-setting enums. This is the
 * pattern leagues-v2 establishes for round/course options (Option B) — it wraps
 * the app's canonical `Option<T>` data in shadcn `Select` + `Field` primitives,
 * replacing the legacy react-select `SelectFormField`/`EnumSelect`.
 *
 * Radix Select speaks strings; option values are numbers/booleans/strings, so we
 * serialize to `String(value)` for the item and resolve back to the original
 * typed value on change (keeps enum ints and booleans intact in the form state).
 */
export function RoundSettingSelect({
  name,
  label,
  options,
  description,
  placeholder = "Select…",
  disabled,
}: RoundSettingSelectProps) {
  const { control } = useFormContext();
  return (
    <Field>
      <FieldLabel htmlFor={name}>{label}</FieldLabel>
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          const selected = options.find((o) => o.value === field.value);
          return (
            <Select
              value={selected ? String(selected.value) : undefined}
              onValueChange={(v) => {
                const picked = options.find((o) => String(o.value) === v);
                field.onChange(picked ? picked.value : v);
              }}
              disabled={disabled}
            >
              <SelectTrigger
                id={name}
                className="w-full rounded-lg bg-surface border-border"
              >
                {/* Render the label ourselves: Radix only mounts the items (and
                    thus its auto text lookup) when the dropdown opens, so a
                    controlled value would otherwise show the raw value until
                    first open. `selected` is derived from the current value. */}
                <SelectValue placeholder={placeholder}>
                  {selected?.label}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="rounded-lg">
                {options.map((o) => (
                  <SelectItem
                    key={String(o.value)}
                    value={String(o.value)}
                    className="rounded-lg"
                  >
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        }}
      />
      {description && <FieldDescription>{description}</FieldDescription>}
    </Field>
  );
}
